import { useState, useEffect } from 'react';
import { reportsApi } from '../api/reports.api';
import {
  MonthlySummary,
  DailySummary,
  DriverStats,
  YearlySummary,
  YearlyDrivers,
  YearlyVehicles,
} from '../types/report';

export const useMonthlySummary = (month: string, driverId?: string) => {
  const [data, setData] = useState<MonthlySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMonthlySummary = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await reportsApi.getMonthlySummary(month, driverId);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch monthly summary');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlySummary();
  }, [month, driverId]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchMonthlySummary,
  };
};

export const useDailySummary = (startDate: string, endDate: string, driverId?: string) => {
  const [data, setData] = useState<DailySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDailySummary = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await reportsApi.getDailySummary(startDate, endDate, driverId);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch daily summary');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchDailySummary();
    }
  }, [startDate, endDate, driverId]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchDailySummary,
  };
};

export const useYearlySummary = (year: number) => {
  const [data, setData] = useState<YearlySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await reportsApi.getYearlySummary(year);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch yearly summary');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, [year]);

  return { data, isLoading, error, refetch: fetch };
};

export const useYearlyDrivers = (year: number) => {
  const [data, setData] = useState<YearlyDrivers | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await reportsApi.getYearlyDrivers(year);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch yearly drivers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, [year]);

  return { data, isLoading, error, refetch: fetch };
};

export const useYearlyVehicles = (year: number) => {
  const [data, setData] = useState<YearlyVehicles | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await reportsApi.getYearlyVehicles(year);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch yearly vehicles');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, [year]);

  return { data, isLoading, error, refetch: fetch };
};

export const useDriverStats = (startDate: string, endDate: string, driverId?: string) => {
  const [data, setData] = useState<DriverStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDriverStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await reportsApi.getDriverStats(startDate, endDate, driverId);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch driver stats');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchDriverStats();
    }
  }, [startDate, endDate, driverId]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchDriverStats,
  };
};
